// 12章 木工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_MOISTURE = `
<svg viewBox="0 0 660 230" role="img" aria-label="木材の含水率と寸法の考え方。含水率は特記がなければA種で下地材造作材とも15パーセント以下。寸法は引出線で短辺かける長辺が示されればひき立て寸法、寸法線ならば仕上り寸法">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30"  y="60" width="290" height="120" rx="10"/>
    <rect x="340" y="60" width="300" height="120" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold">
    <text x="50" y="88">含水率（特記がなければA種）</text>
    <text x="360" y="88">寸法の表し方</text>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="50" y="118">下地材：15%以下（B種20%以下）</text>
    <text x="50" y="142">造作材：15%以下（B種18%以下）</text>
    <text x="50" y="168" opacity="0.8">乾いた木でないと後で反り・すきが出る</text>
    <text x="360" y="116">引出線（短辺×長辺）</text>
    <text x="372" y="134" opacity="0.85">→ ひき立て寸法（削る前）</text>
    <text x="360" y="158">寸法線（部材寸法）</text>
    <text x="372" y="176" opacity="0.85">→ 仕上り寸法（削った後）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="30" y="45">木は乾くと縮む。含水率管理と寸法の読み方が木工事の出発点</text>
    <text x="30" y="212">見え掛り面はプレーナー加工仕上げ程度（12.1.4）。材料はF☆☆☆☆等でシックハウス対策</text>
  </g>
</svg>`;

const SVG_NAIL = `
<svg viewBox="0 0 660 220" role="img" aria-label="釘打ちのルール。釘の長さは打ち付ける板厚の2.5倍以上。造作材が下地材と平行する場合は間隔300から450ミリ程度、幅の広い板は両耳とその中間に間隔100ミリ程度。化粧面は隠し釘打ちが標準">
  <g fill="currentColor" opacity="0.3"><rect x="60" y="120" width="200" height="30"/></g>
  <g fill="currentColor" opacity="0.55"><rect x="60" y="100" width="200" height="20"/></g>
  <g stroke="currentColor" stroke-width="3" stroke-linecap="round">
    <line x1="100" y1="90" x2="100" y2="145"/>
    <line x1="160" y1="90" x2="160" y2="145"/>
    <line x1="220" y1="90" x2="220" y2="145"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="290" y1="100" x2="290" y2="150"/>
    <line x1="282" y1="100" x2="298" y2="100"/>
    <line x1="282" y1="150" x2="298" y2="150"/>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="308" y="118" font-weight="bold">釘長さ ≧ 板厚の2.5倍</text>
    <text x="308" y="140" opacity="0.85">（薄く効かせると効かない）</text>
    <text x="60" y="180">・下地材と平行：間隔300〜450mm程度</text>
    <text x="60" y="202">・幅の広い板：両耳＋中間に間隔100mm程度／化粧面は隠し釘打ちが標準</text>
  </g>
</svg>`;

const SVG_CEILING = `
<svg viewBox="0 0 660 260" role="img" aria-label="天井下地の構成。スラブに打ち込んだインサートにつりボルトをねじ込み、野縁受け桟を吊り、それに直交して野縁を取り付ける。野縁にせっこうボード等を張る。つりボルトと野縁受けの間隔は900ミリ程度、野縁は450ミリ程度">
  <g fill="currentColor" opacity="0.25"><rect x="40" y="40" width="580" height="24"/></g>
  <g stroke="currentColor" stroke-width="3" stroke-linecap="round">
    <line x1="160" y1="64" x2="160" y2="150"/>
    <line x1="500" y1="64" x2="500" y2="150"/>
  </g>
  <g fill="currentColor" opacity="0.7">
    <rect x="120" y="150" width="420" height="16"/>
  </g>
  <g fill="currentColor" opacity="0.85">
    <rect x="180" y="166" width="24" height="20"/>
    <rect x="330" y="166" width="24" height="20"/>
    <rect x="480" y="166" width="24" height="20"/>
  </g>
  <g fill="currentColor" opacity="0.45">
    <rect x="120" y="200" width="420" height="18"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="80" y="55" opacity="0.85">スラブ・インサート</text>
    <text x="175" y="120" font-weight="bold">つりボルト</text>
    <text x="175" y="138" opacity="0.8">呼び径9mm・間隔900mm程度</text>
    <text x="360" y="145" opacity="0.85">野縁受桟（間隔900mm）</text>
    <text x="360" y="182" opacity="0.85">野縁（間隔450mm）</text>
    <text x="360" y="214" opacity="0.85">せっこうボード等の仕上げ</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="245">上げ下げが調整できるつりボルトで水平を出す。開口部（照明・点検口）は同材で補強（12.7.2）</text>
  </g>
</svg>`;

const ch12: Record<string, Commentary> = {
  "12.1.1": {
    plainSummary:
      "この章はRC造・S造・組積造等における内装の木下地・木造作・木仕上げの工事に適用する。1章とセットで適用する。",
    why: "この章は木造建築ではなく、鉄筋コンクリート造等の建物の中の木の仕事（下地・枠・造作・床板・天井下地）を扱います。木は乾くと縮む生き物なので、含水率・継手・養生の管理が品質を左右します。",
  },

  "12.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②造作材が所定の形状・寸法で所定の位置に架構され仕上り面が所要の状態 ③下地材が所定の方法で固定され、床は床鳴りが生じないこと。",
    why: "「床鳴りが生じないこと」がわざわざ品質として書かれているのが木工事の特徴。床鳴りは施工不良（釘の効き不足・材の乾燥不足）のサインで、クレームの定番です。",
  },

  "12.1.3": {
    plainSummary:
      "木材の断面寸法は、引出線で部材寸法（短辺×長辺）が示されていればひき立て寸法、寸法線で記入されていれば仕上り寸法とする。指定がなければひき立て寸法とする。",
    why: "木材は削って仕上げると小さくなります。図面の寸法が「削る前（ひき立て）」か「削った後（仕上り）」かで、発注する材のサイズが変わります。図面の書き方（引出線か寸法線か）で読み分けるのがプロの知識です。",
    glossary: [
      {
        term: "ひき立て寸法",
        meaning: "製材したまま（仕上げ削り前）の寸法。",
      },
      {
        term: "仕上り寸法",
        meaning: "かんな等で削って仕上げた後の寸法。ひき立てより小さい。",
      },
    ],
    diagrams: [
      {
        svg: SVG_MOISTURE,
        caption: "含水率と寸法の表し方（12.1.3・12.2.1）",
      },
    ],
  },

  "12.1.4": {
    plainSummary:
      "見え掛り面の表面はプレーナー加工仕上げ程度とする（超自動機械かんな・サンダー等による場合は特記）。",
    glossary: [
      {
        term: "プレーナー加工",
        meaning: "自動かんな盤で木材表面を平滑に削る加工。",
      },
      {
        term: "見え掛り",
        meaning: "仕上げ後も目に見える面。隠れる面は「見え隠れ」。",
      },
    ],
  },

  "12.1.5": {
    plainSummary:
      "継手は乱（ばらばらの位置）に配置する。土台等でやむを得ず短材を使う場合は1ｍを限度とする。合板・ボード等の壁付き材は小穴じゃくりをつける。継手・仕口が明示されていない場合は適切な工法を定めて監督職員に報告する。",
    why: "継手を一直線に揃えると、その線が弱点になります。「乱に配置」して弱点を分散させるのは、鉄筋の継手をずらすのと同じ考え方です。",
    glossary: [
      {
        term: "継手・仕口",
        meaning:
          "木材どうしの接合部。同方向につなぐのが継手、直交・交差させるのが仕口。",
      },
      {
        term: "小穴じゃくり",
        meaning: "板の端を溝状に加工して、他材にはめ込む納まり。",
      },
    ],
  },

  "12.1.6": {
    plainSummary:
      "造作材・仕上材はハトロン紙・ビニル加工紙等で包装するなどして汚損しないよう養生する。集積場所は直射日光・高温・多湿を避ける。",
    why: "木材は湿気を吸って反り、日光で変色します。仕上げの木は特にデリケートなので、包装と保管環境が仕上がりを左右します。",
  },

  "12.2.1": {
    plainSummary:
      "木材・合板は品質・含水率・出荷量を記録した出荷証明書を提出する。含水率は特記がなければA種（下地材・造作材とも15%以下）。ホルムアルデヒド放散量はF☆☆☆☆等。製材はJAS製材、造作用集成材等はJAS集成材による（等級・寸法・含水率は特記）。",
    why: "含水率15%以下（A種）は、室内で使う木材が後から縮んで隙間・反りを出さないための基準です。乾いていない木を使うと、数か月後に必ず不具合が出ます。出荷証明書は乾燥度の裏付けです。",
    points: [
      "含水率A種15%以下は木工事の基本数値",
      "出荷証明書で品質・含水率を確認",
    ],
    glossary: [
      {
        term: "含水率",
        meaning:
          "木材に含まれる水分の割合。高いと乾燥時に収縮・反り・割れが出る。",
      },
      {
        term: "造作材",
        meaning: "枠・額縁・敷居・かもいなど、仕上げに見える木材。",
      },
    ],
  },

  "12.2.2": {
    plainSummary:
      "釘はJIS A 5508により、湿潤箇所はステンレス製・それ以外は表面処理鉄製。釘の長さは打ち付ける板厚の2.5倍以上を標準とする。造作材の釘打ちは等間隔で、下地材と平行する場合は間隔300〜450mm程度、幅の広い板は両耳と中間に間隔100mm程度。化粧面は隠し釘打ちが標準。諸金物（かすがい・座金・箱金物等）は亜鉛めっき品。",
    why: "釘は「板厚の2.5倍以上」という長さの目安が重要。短い釘は下地に十分効かず、抜けや床鳴りの原因になります。化粧面（見える面）に釘頭が出ないよう隠し釘にするのは、仕上げの美しさのためです。",
    points: [
      "釘長さ＝板厚の2.5倍以上",
      "湿潤箇所（水回り等）の釘はステンレス（錆で木が割れる）",
      "化粧面は隠し釘打ちが標準",
    ],
    diagrams: [
      {
        svg: SVG_NAIL,
        caption: "釘打ちのルール（12.2.2）",
      },
    ],
  },

  "12.2.3": {
    plainSummary:
      "木れんがは接着工法またはあと施工アンカーで取り付ける。接着剤はJIS A 5537（木れんが用接着剤）による（ホルムアルデヒド放散量は特記がなければF☆☆☆☆）。",
    glossary: [
      {
        term: "木れんが",
        meaning:
          "コンクリート壁に埋め込む・接着する木片。造作材を釘留めするための下地になる。",
      },
    ],
  },

  "12.3.1": {
    plainSummary:
      "木材の防腐・防蟻処理は特記による。工場での加圧注入処理はJAS保存処理の性能区分K2〜K4に適合するもの（薬剤・注入量の証明書を提出）。加圧注入後に切断・孔あけした箇所は塗布処理を行う。薬剤はJIS K 1570/1571に適合するものを使う。",
    why: "土台や水回りの木材は腐朽菌とシロアリの標的です。加圧注入は薬剤を木の芯まで浸透させる工場処理で、塗布より効果が高い。切断面は薬剤の入っていない部分が露出するので、現場で追加処理が必要です。",
    glossary: [
      {
        term: "加圧注入処理",
        meaning:
          "圧力をかけて木材内部に防腐・防蟻薬剤を浸透させる工場処理。K2〜K4は性能区分。",
      },
      {
        term: "防蟻（ぼうぎ）",
        meaning: "シロアリの食害を防ぐ処理。",
      },
    ],
  },

  "12.3.2": {
    plainSummary:
      "防虫処理の適用は特記。ラワン材・ならを使う場合はJAS保存処理の性能区分K1の防虫処理を行う。",
    why: "ラワンやならは虫（ヒラタキクイムシ等）が付きやすい樹種のため、防虫処理が特に求められます。",
  },

  "12.3.3": {
    plainSummary:
      "不燃材料・準不燃材料・難燃材料を使用する場合は特記による。",
  },

  "12.4.1": {
    plainSummary:
      "間仕切軸組・床組に用いる木材は特記がなければ杉または松。ただし土間スラブの類の土台・転ばし大引・転ばし根太はひのきまたは保存処理木材とする。",
    why: "土間や水回りに接する部材は腐りやすいため、耐久性の高いひのきや保存処理材に限定されます。乾いた場所は杉・松でよい、という使い分けです。",
  },

  "12.4.2": {
    plainSummary:
      "間仕切軸組・床組の工法は表12.4.1による。土台・柱・間柱・まぐさ窓台・大引・根太・床束等の継手・仕口・取付け方法が細かく定められている（アンカーボルト間隔1.8ｍ程度等）。コンクリート・壁との取合いは両端を押さえ間隔900mm程度にくさびをかい水平垂直を正して釘打ち。",
    why: "この表は木下地の「組み立て説明書」です。腰掛あり継ぎ・短ほぞ差し・渡りあご掛けといった伝統的な継手・仕口の名前が並びます。アンカーボルト1.8ｍ間隔などの数値は、下地がしっかり躯体に固定されるための基準です。",
    glossary: [
      {
        term: "間仕切軸組",
        meaning: "間仕切壁の骨組み（土台・柱・間柱・頭つなぎ等）。",
      },
      {
        term: "大引・根太",
        meaning:
          "床を支える横材。大引の上に直交して根太を渡し、その上に床板を張る。",
      },
      {
        term: "短ほぞ差し",
        meaning: "部材の端に作った短い突起（ほぞ）を相手の穴に差し込む仕口。",
      },
    ],
  },

  "12.5.1": {
    plainSummary:
      "窓・出入口その他に用いる木材は特記がなければ、吊元枠・水掛りの下枠・敷居はひのき、その他は松または杉。",
    why: "敷居や水掛りの下枠は摩耗・水濡れに強いひのきを使う、というのが伝統的な適材適所です。",
  },

  "12.5.2": {
    plainSummary:
      "窓・出入口その他の工法は表12.5.1による。縦枠・上枠・下枠・敷居・かもい等の下ごしらえ（戸当りじゃくり・戸溝じゃくり）、組立（えり輪入れ短ほぞ差し等）、取付け（木造壁・軽量鉄骨壁・コンクリート壁ごと）が定められている。コンクリート等に接する枠幅180mm以上等の枠裏は防湿処理を行う。",
    why: "枠は建具（ドア・窓）を受ける重要な下地です。開き戸には戸当りじゃくり、引違い戸には戸溝じゃくりと、建具の種類で加工が変わります。コンクリートに接する枠裏の防湿処理は、コンクリートの湿気で枠が腐るのを防ぎます。",
    glossary: [
      {
        term: "かもい・敷居",
        meaning: "引き戸の上下のレール溝を持つ横木。上がかもい、下が敷居。",
      },
      {
        term: "戸当りじゃくり",
        meaning: "開き戸が閉まる位置に設ける段欠き加工。戸が行き過ぎないよう受ける。",
      },
    ],
  },

  "12.6.1": {
    plainSummary:
      "縁甲板・上がりがまちに用いる木材は特記がなければひのき。フローリングは19章5節による。",
    glossary: [
      {
        term: "上がりがまち",
        meaning: "玄関の上がり口など、床の段差の見切りに入れる横木。",
      },
    ],
  },

  "12.6.2": {
    plainSummary:
      "床板張りの工法は表12.6.1による。下張り用床板（構造用合板厚12mm等、留付け間隔は継手部150mm・中間部200mm程度）、二重張り用合板、畳下床板、縁甲板張り（板幅120mm程度・そばさねはぎ・隠し釘打ち）等。フローリング張りは19章5節による。",
    why: "床は下張り＋仕上げの二重構造が基本。下張りの継手位置と仕上げの継手位置をずらすのは、継目が重なって弱点や隙間になるのを防ぐためです。「そばさねはぎ」は板の側面を凹凸にして継ぐ納まりで、隙間と段差を防ぎます。",
    glossary: [
      {
        term: "縁甲板（えんこういた）",
        meaning: "縁側や床に張る細長い板。実（さね）で継ぐ。",
      },
      {
        term: "さねはぎ",
        meaning:
          "板の側面に凸凹（実）を加工してはめ合わせる継ぎ方。段差・隙間を防ぐ。",
      },
    ],
  },

  "12.7.1": {
    plainSummary: "壁及び天井下地に用いる木材は特記がなければ杉または松。",
  },

  "12.7.2": {
    plainSummary:
      "壁及び天井下地の工法は表12.7.1による。壁胴縁（15×45mm、せっこうボードは間隔300mm程度）、野縁受桟（30×40mm、間隔900mm程度）、野縁（30×40mm、間隔450mm程度）、吊木・つりボルト（呼び径9mm、間隔900mm程度、インサートに取付け）等。開口部（照明器具・点検口・壁等）は同材で補強する。",
    why: "天井下地は「インサート→つりボルト→野縁受→野縁→ボード」の階層構造です。つりボルトで高さを調整して天井の水平を出します。照明やダクトの開口部を同材で補強するのは、開口周りの野縁が切れて弱くなるのを防ぐためです。ボードの継目位置には野縁を2本抱き合わせて確実に受けます。",
    points: [
      "野縁の間隔450mm程度、つりボルト・野縁受は900mm程度",
      "ボード継目位置の野縁は2本抱き合わせ",
      "照明・点検口・ダクトの開口部は同材で補強",
    ],
    glossary: [
      {
        term: "胴縁（どうぶち）",
        meaning: "壁のボードを留めるために柱・間柱に取り付ける横桟。",
      },
      {
        term: "野縁（のぶち）",
        meaning: "天井ボードを留めるために吊る格子状の下地材。",
      },
      {
        term: "インサート",
        meaning:
          "コンクリートスラブに打ち込んでおく、つりボルトをねじ込むための金物。",
      },
    ],
    diagrams: [
      {
        svg: SVG_CEILING,
        caption: "天井下地の構成（12.7.2）",
      },
    ],
  },
};

export default ch12;
